"use client";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import clsx from "clsx";
import { Button } from "components/ui/button";
import { observer } from "mobx-react-lite";
import { Combo as ComboType } from "interfaces/combos";
import { useState } from "react";
import { Drawer } from "./ui/drawer";
import AddComboOrderDrawerContent from "./add-combo-order-drawer-content";
import cart from "observables/cart";
import BlurFade from "./ui/blur-fade";
import { Badge } from "components/ui/badge";
import { Plus, Sparkles, Star } from "lucide-react";

interface Props {
  combo: ComboType;
  className?: string;
  onClickAddCombo?: (variantIds: Array<number>) => void;
}

const Combo = ({ combo, className, onClickAddCombo }: Props) => {
  const [isComboDrawerOpen, setIsComboDrawerOpen] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState(false);

  const handleClickAddCombo = () => {
    setIsComboDrawerOpen(true);
  };

  const handleComboAdded = () => {
    setIsAdding(true);
    setTimeout(() => {
      setIsAdding(false);
    }, 500);
  };

  const hasPromotion = cart.promotion > 0;

  return (
    <>
      <Card
        className={clsx(
          className,
          `group relative overflow-hidden transition-all duration-300 ease-in-out
          hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1
          border-0 bg-gradient-to-br from-white via-amber-50/30 to-white
          dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900
          backdrop-blur-sm flex flex-col
          ${isAdding ? 'shadow-2xl shadow-amber-500/25 ring-2 ring-amber-400/50' : ''}`
        )}
      >
        {/* Promotion badge */}
        {hasPromotion && (
          <div className="absolute top-3 right-3 z-20">
            <Badge
              variant="secondary"
              className="bg-gradient-to-r from-red-500 to-pink-500 text-white border-0 shadow-lg"
            >
              <Sparkles className="w-3 h-3 mr-1" />-{cart.promotion}%
            </Badge>
          </div>
        )}

        {/* Large Cover Image Section with Overlaid Content */}
        {combo.pictures?.[0]?.picture && (
          <div className="relative h-64 md:h-72 overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700">
            {/* Background Image */}
            <BlurFade
              key={combo.pictures?.[0].picture.path}
              delay={0.25 + Math.random() * 10 * 0.05}
              className="absolute inset-0 w-full h-full"
            >
              <img
                src={combo.pictures?.[0].picture.path}
                alt={combo.name}
                className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
              />
            </BlurFade>

            {/* Gradient Overlay for Text Readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

            {/* Overlaid Content */}
            <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-between">
              {/* Top Section - Combo Info with proper spacing */}
              <div className="flex justify-between items-start mt-12">
                <div className="flex-1">
                  {/* Special combo badge */}
                  <Badge
                    variant="secondary"
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg"
                  >
                    <Star className="w-3 h-3 mr-1" />
                    Formule
                  </Badge>
                  <CardTitle className="text-xl md:text-2xl font-bold text-white drop-shadow-lg">
                    {combo.name}
                  </CardTitle>
                  <CardDescription className="text-sm md:text-base font-medium text-amber-200 mt-1 drop-shadow">
                    {"Formule complète"}
                  </CardDescription>
                </div>

                <div className="text-right">
                  {hasPromotion ? (
                    <div className="space-y-1">
                      <div className="text-sm md:text-base text-gray-300 line-through drop-shadow">
                        {combo.priceAfterVat}€
                      </div>
                      <div className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                        {(
                          Number(combo.priceAfterVat) *
                          (1 - cart.promotion / 100)
                        ).toFixed(2)}
                        €
                      </div>
                    </div>
                  ) : (
                    <div className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                      {combo.priceAfterVat}€
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Section - Combo Elements */}
              <div className="space-y-3">
                <div className="text-xs md:text-sm font-semibold text-white/90 uppercase tracking-wide drop-shadow">
                  Inclus dans la formule
                </div>
                <div className="space-y-1.5">
                  {combo.elements.slice(0, 4).map((element) => (
                    <div key={element.id} className="flex items-center">
                      <div className="w-2 h-2 bg-amber-300 rounded-full mr-3 flex-shrink-0 drop-shadow"></div>
                      <span className="text-sm md:text-base text-white leading-tight drop-shadow font-medium">
                        {(element.variant?.name &&
                          `${element.variant?.product.name} ${element.variant?.name}`) ||
                          element.product?.name ||
                          element.productType?.name ||
                          element.comboCustomList?.name}
                      </span>
                    </div>
                  ))}
                  {combo.elements.length > 4 && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-amber-300 rounded-full mr-3 flex-shrink-0 drop-shadow"></div>
                      <span className="text-sm md:text-base text-white/80 leading-tight drop-shadow font-medium">
                        +{combo.elements.length - 4} autres éléments
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fallback for combos without images */}
        {!combo.pictures?.[0]?.picture && (
          <>
            <CardHeader className="relative z-10 pb-2">
              <div className="flex flex-col space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {/* Special combo badge */}
                    <Badge
                      variant="secondary"
                      className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg"
                    >
                      <Star className="w-3 h-3 mr-1" />
                      Formule
                    </Badge>
                    <CardTitle className="text-lg font-bold text-gray-900 dark:text-gray-100 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors duration-200">
                      {combo.name}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-amber-600/80 dark:text-amber-400/80 mt-1">
                      {"Formule complète"}
                    </CardDescription>
                  </div>

                  <div className="text-right">
                    {hasPromotion ? (
                      <div className="space-y-1">
                        <div className="text-sm text-gray-400 dark:text-gray-500 line-through">
                          {combo.priceAfterVat}€
                        </div>
                        <div className="text-2xl font-bold bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent">
                          {(
                            Number(combo.priceAfterVat) *
                            (1 - cart.promotion / 100)
                          ).toFixed(2)}
                          €
                        </div>
                      </div>
                    ) : (
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {combo.priceAfterVat}€
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="relative z-10 py-4 flex-1">
              <div className="bg-amber-50/80 dark:bg-gray-800/80 rounded-lg p-3 max-h-28 overflow-auto">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide text-center">
                  Inclus dans la formule
                </div>
                <div className="space-y-1">
                  {combo.elements.map((element) => (
                    <div
                      key={element.id}
                      className="flex items-center justify-center"
                    >
                      <div className="w-2 h-2 bg-amber-500 dark:bg-amber-400 rounded-full mr-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700 dark:text-gray-300 leading-tight text-center">
                        {(element.variant?.name &&
                          `${element.variant?.product.name} ${element.variant?.name}`) ||
                          element.product?.name ||
                          element.productType?.name ||
                          element.comboCustomList?.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </>
        )}

        <CardFooter className="relative z-10 pt-4">
          <Button
            onClick={handleClickAddCombo}
            className={`
              w-full h-12 font-semibold rounded-lg shadow-lg hover:shadow-xl 
              transition-all duration-500 transform
              ${isAdding
                ? `bg-gradient-to-r from-emerald-400 via-green-500 to-emerald-600 
                   animate-pulse shadow-2xl shadow-emerald-500/50 
                   ring-4 ring-emerald-400/30 ring-offset-2 ring-offset-white dark:ring-offset-gray-900
                   scale-105 hover:scale-105`
                : `bg-gradient-to-r from-amber-600 to-orange-600 
                   hover:from-amber-700 hover:to-orange-700 
                   dark:from-amber-700 dark:to-orange-700 dark:hover:from-amber-800 dark:hover:to-orange-800 
                   hover:scale-[1.02] hover:shadow-amber-500/25`
              } 
              text-white relative overflow-hidden
            `}
          >
            {/* Magical shimmer effect */}
            <div className={`absolute inset-0 ${isAdding ? 'animate-pulse' : 'opacity-0'} bg-gradient-to-r from-transparent via-white/30 to-transparent transform skew-x-12`}></div>
            
            {isAdding ? (
              <div className="flex items-center justify-center relative z-10">
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    {/* Magic star burst effect */}
                    <div className="absolute inset-0 animate-ping">
                      <Star className="w-5 h-5 text-yellow-300 animate-spin" />
                    </div>
                    <Star className="w-5 h-5 text-white animate-bounce" />
                  </div>
                  <span className="animate-bounce font-bold tracking-wide">
                    Formule ajoutée !
                  </span>
                  <div className="flex space-x-1">
                    <div className="w-1.5 h-1.5 bg-yellow-300 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-orange-300 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-1.5 h-1.5 bg-amber-300 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center relative z-10 group-hover:scale-105 transition-transform duration-200">
                <div className="relative">
                  <Plus className="w-4 h-4 mr-2 group-hover:rotate-180 transition-transform duration-500" />
                  {/* Subtle sparkle on hover */}
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-300"></div>
                </div>
                <span className="tracking-wide">Choisir cette formule</span>
              </div>
            )}
          </Button>
        </CardFooter>
      </Card>

      <Drawer
        open={isComboDrawerOpen}
        onOpenChange={(isOpen: boolean) => setIsComboDrawerOpen(isOpen)}
      >
        <AddComboOrderDrawerContent
          comboId={combo.id}
          comboName={combo.name}
          comboPriceAfterVat={combo.priceAfterVat}
          onCloseDrawer={() => setIsComboDrawerOpen(false)}
          isComboDrawerOpen={isComboDrawerOpen}
          onClickAddCombo={(variantIds) => {
            onClickAddCombo?.(variantIds);
            handleComboAdded();
          }}
        />
      </Drawer>
    </>
  );
};

export default observer(Combo);
